import { useEffect, useState, type FormEvent } from 'react';
import { Loader2, Plus, Trash2, UploadCloud } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { toast } from 'sonner';
import { api, ApiError } from '@/services/api';
import { useApplicants } from '@/contexts/ApplicantContext';
import { Card } from '@/components/ui/Card';
import { OnboardingModal } from '@/components/OnboardingModal';
import { ConnectedAccountsCard } from '@/components/ConnectedAccountsCard';
import { useApplicants as useApplicantContext } from '@/contexts/ApplicantContext';

// Cap visible to the user; matches the "2-3 people using this system" brief.
// Existing applicants show normally; remaining count up to 3 render as blank
// "+ drop resume to add" slots so onboarding stays discoverable.
const TOTAL_USER_SLOTS = 3;

interface SettingsBody {
  settable: string[];
  values: Record<string, string>;
}

export default function SettingsPage() {
  const { applicants, refresh } = useApplicants();
  const [data, setData] = useState<SettingsBody | null>(null);
  const [dirty, setDirty] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [onboardOpen, setOnboardOpen] = useState(false);
  // Keep these for the rare "force manual add" path (kept hidden behind details).
  const [addOpen, setAddOpen] = useState(false);
  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newCap, setNewCap] = useState(50);
  const [adding, setAdding] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get<SettingsBody>('/settings');
      setData(r);
      setDirty({});
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function save() {
    if (Object.keys(dirty).length === 0) return;
    try {
      await api.post('/settings', dirty);
      toast.success('Settings saved — restart workers to pick up changes');
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'save_failed');
    }
  }

  async function togglePause(id: string, paused: boolean) {
    try {
      await api.post(`/applicants/${id}/pause`, { paused });
      toast.success(paused ? 'Paused' : 'Resumed');
      refresh();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'toggle_failed');
    }
  }

  async function addApplicant(e: FormEvent) {
    e.preventDefault();
    if (!newId || !newName || !newEmail) return;
    setAdding(true);
    try {
      await api.post('/applicants', {
        applicant_id: newId.toLowerCase(),
        display_name: newName,
        email: newEmail,
        daily_cap: newCap,
      });
      toast.success(`Added applicant ${newId}`);
      setAddOpen(false);
      setNewId('');
      setNewName('');
      setNewEmail('');
      setNewCap(50);
      refresh();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'add_failed');
    } finally {
      setAdding(false);
    }
  }

  async function setCap(id: string, daily_cap: number) {
    try {
      await api.patch(`/applicants/${id}`, { daily_cap });
      toast.success('Daily cap updated');
      refresh();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'update_failed');
    }
  }

  async function removeApplicant(id: string) {
    let summary: { applied: number; failed: number; pending: number; total_jobs: number; signals: number };
    try {
      summary = await api.get(`/applicants/${id}/delete-summary`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'summary_failed');
      return;
    }
    const lines = [
      `Delete applicant "${id}"?`,
      ``,
      `This will remove ${summary.total_jobs} job rows (${summary.applied} applied, ${summary.failed} failed, ${summary.pending} pending)`,
      `and ${summary.signals} interview signals + all on-disk resumes/profile dirs.`,
    ];
    if (summary.applied > 0) {
      lines.push('', '⚠ This applicant has APPLIED jobs. Deleting will lose application history.');
    }
    if (!confirm(lines.join('\n'))) return;
    try {
      await api.delete(`/applicants/${id}${summary.applied > 0 ? '?force=1' : ''}`);
      toast.success(`Removed applicant ${id}`);
      refresh();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'delete_failed');
    }
  }

  return (
    <div className="space-y-5">
      <h2 className="text-xl font-semibold tracking-tight">Settings</h2>

      <Card
        title="Applicants"
        action={
          <button onClick={() => setOnboardOpen(true)} className="btn-primary text-xs">
            <UploadCloud className="h-3.5 w-3.5" />
            Add by resume
          </button>
        }
      >
        <ul className="space-y-3">
          {applicants.map((a) => (
            <li key={a.applicant_id} className="rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-800/60">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="font-medium text-gray-800 dark:text-gray-100">{a.display_name}</div>
                  <div className="text-xs text-gray-500">{a.email}</div>
                </div>
                <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
                  Daily cap
                  <input
                    type="number"
                    defaultValue={a.daily_cap}
                    min={0}
                    max={500}
                    className="input w-20"
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (v !== a.daily_cap) setCap(a.applicant_id, v);
                    }}
                  />
                </label>
                <button
                  onClick={() => togglePause(a.applicant_id, a.paused === 0)}
                  className={a.paused === 1 ? 'btn-primary' : 'btn-secondary'}
                >
                  {a.paused === 1 ? 'Resume' : 'Pause'}
                </button>
                <button
                  onClick={() => removeApplicant(a.applicant_id)}
                  className="rounded-md p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                  title="Delete applicant (irreversible — removes jobs + on-disk profile)"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-gray-500">Email notifications</summary>
                <div className="pt-2">
                  <NotifyEmailField applicant={a} onSaved={refresh} />
                </div>
              </details>
            </li>
          ))}
          {Array.from({
            length: Math.max(0, TOTAL_USER_SLOTS - applicants.length),
          }).map((_, i) => (
            <li key={`empty-${i}`}>
              <button
                onClick={() => setOnboardOpen(true)}
                className="flex w-full items-center justify-center gap-2 rounded-md border-2 border-dashed border-gray-300 px-3 py-4 text-sm text-gray-500 transition hover:border-blue-400 hover:bg-blue-50 dark:border-gray-700 dark:hover:bg-blue-900/20"
              >
                <UploadCloud className="h-4 w-4" />
                Add user — drop a resume PDF
              </button>
            </li>
          ))}
        </ul>
        <details className="mt-3 text-xs text-gray-500">
          <summary className="cursor-pointer">
            Fallback: add an applicant manually (advanced)
          </summary>
          <button
            onClick={() => setAddOpen(true)}
            className="mt-1 text-blue-600 hover:underline"
          >
            <Plus className="inline h-3 w-3" /> Open manual form
          </button>
        </details>
      </Card>

      <OnboardingModal
        open={onboardOpen}
        onClose={() => setOnboardOpen(false)}
        onCreated={() => refresh()}
      />

      <ConnectedAccountsForCurrent />

      <ChangePasswordCard />

      {/* Everything below is collapsed by default — power-user knobs. */}
      <details className="rounded-lg border border-gray-200 dark:border-gray-800">
        <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200">
          Advanced
        </summary>
        <div className="space-y-4 border-t border-gray-100 p-4 dark:border-gray-800">
          <Card
            title="Feature flags"
            action={
              <button onClick={save} disabled={Object.keys(dirty).length === 0} className="btn-primary text-xs">
                Save {Object.keys(dirty).length > 0 && `(${Object.keys(dirty).length})`}
              </button>
            }
          >
            {loading || !data ? (
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            ) : (
              <ul className="space-y-2">
                {data.settable.map((key) => {
                  const current = dirty[key] ?? data.values[key] ?? '';
                  return (
                    <li key={key} className="flex items-center justify-between gap-3">
                      <code className="text-xs text-gray-600">{key}</code>
                      <input
                        value={current}
                        onChange={(e) => setDirty((d) => ({ ...d, [key]: e.target.value }))}
                        className="input w-40 text-right"
                      />
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="mt-3 text-xs text-gray-500">
              These write to <code>jobapplier/.env</code>. Workers + broker re-read on next start.
            </p>
          </Card>

          <BackupCard />
          <AuditLogCard />

          <Card title="Internal pages">
            <ul className="space-y-1 text-sm">
              <li>
                <a href="/queue" className="text-blue-600 hover:underline">
                  Queue
                </a>
                <span className="ml-2 text-xs text-gray-500">
                  manually add ATS URLs, broker stats
                </span>
              </li>
              <li>
                <a href="/workers" className="text-blue-600 hover:underline">
                  Workers
                </a>
                <span className="ml-2 text-xs text-gray-500">
                  worker health, cooldowns, capacity
                </span>
              </li>
            </ul>
          </Card>
        </div>
      </details>

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add applicant"
        footer={
          <>
            <button onClick={() => setAddOpen(false)} className="btn-secondary">
              Cancel
            </button>
            <button onClick={addApplicant} disabled={adding} className="btn-primary">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
            </button>
          </>
        }
      >
        <form onSubmit={addApplicant} className="space-y-3">
          <label className="block text-xs font-medium text-gray-700">
            Applicant ID (lowercase, no spaces)
            <input
              value={newId}
              onChange={(e) => setNewId(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              className="input mt-1"
              placeholder="alex"
              required
            />
          </label>
          <label className="block text-xs font-medium text-gray-700">
            Display name
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="input mt-1"
              placeholder="Alex Doe"
              required
            />
          </label>
          <label className="block text-xs font-medium text-gray-700">
            Email
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="input mt-1"
              placeholder="alex@example.com"
              required
            />
          </label>
          <label className="block text-xs font-medium text-gray-700">
            Daily cap
            <input
              type="number"
              value={newCap}
              onChange={(e) => setNewCap(+e.target.value)}
              min={0}
              max={500}
              className="input mt-1 w-32"
            />
          </label>
          <p className="text-xs text-gray-500">
            The server will create <code>resumes/{newId || '<id>'}/</code>,{' '}
            <code>profiles/{newId || '<id>'}/</code>,{' '}
            <code>all_resumes/{newId || '<id>'}/</code>, and{' '}
            <code>chrome_profiles/{newId || '<id>'}/</code> automatically. Next:
            switch to this applicant in the top selector → go to{' '}
            <em>Resumes</em> → upload a master DOCX + fill the profile YAML.
          </p>
        </form>
      </Modal>
    </div>
  );
}

interface Applicant {
  applicant_id: string;
  display_name: string;
  email: string;
  daily_cap: number;
  paused: number;
  notify_email: string | null;
  notify_mode?: 'instant' | 'daily';
}

function ConnectedAccountsForCurrent() {
  // Connected accounts (Gmail, browser session) are per-applicant; bind to
  // whoever is selected in the top-right applicant switcher.
  const { current } = useApplicantContext();
  if (!current) return null;
  return <ConnectedAccountsCard applicantId={current} />;
}

function NotifyEmailField({ applicant, onSaved }: { applicant: Applicant; onSaved: () => void }) {
  const [val, setVal] = useState(applicant.notify_email ?? '');
  const [mode, setMode] = useState<'instant' | 'daily'>(applicant.notify_mode ?? 'instant');
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [digesting, setDigesting] = useState(false);
  const initial = applicant.notify_email ?? '';
  const initialMode = applicant.notify_mode ?? 'instant';
  const dirty = val.trim() !== initial.trim() || mode !== initialMode;

  async function save() {
    setBusy(true);
    try {
      await api.patch(`/applicants/${applicant.applicant_id}`, {
        notify_email: val.trim() || null,
        notify_mode: mode,
      });
      toast.success(val.trim() ? 'Tracking inbox saved' : 'Tracking inbox cleared');
      onSaved();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'save_failed');
    } finally {
      setBusy(false);
    }
  }

  async function testIt() {
    if (!initial.trim()) {
      toast.error('Save a tracking inbox first');
      return;
    }
    setTesting(true);
    try {
      const r = await api.post<{ ok: boolean; sent_to?: string; reason?: string }>(
        `/applicants/${applicant.applicant_id}/test-notify`,
      );
      if (r.ok) toast.success(`Test sent to ${r.sent_to}`);
      else toast.error(r.reason ?? 'test_failed');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'test_failed');
    } finally {
      setTesting(false);
    }
  }

  async function sendDigest() {
    setDigesting(true);
    try {
      const r = await api.post<{
        ok: boolean;
        sent_to?: string;
        signal_count?: number;
        reason?: string;
      }>(`/applicants/${applicant.applicant_id}/send-digest`);
      if (r.ok)
        toast.success(
          r.signal_count
            ? `Digest sent: ${r.signal_count} signal${r.signal_count === 1 ? '' : 's'}`
            : 'No new signals to digest',
        );
      else toast.error(r.reason ?? 'digest_failed');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'digest_failed');
    } finally {
      setDigesting(false);
    }
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
      <label className="text-gray-500">Notify email</label>
      <input
        type="email"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="me-tracking@personal.com"
        className="input flex-1 text-xs"
      />
      <select
        value={mode}
        onChange={(e) => setMode(e.target.value as 'instant' | 'daily')}
        className="input text-xs"
        title="Forward each signal immediately, or batch into one daily digest"
      >
        <option value="instant">instant</option>
        <option value="daily">daily digest</option>
      </select>
      <button onClick={save} disabled={!dirty || busy} className="btn-primary text-xs">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
      </button>
      <button
        onClick={testIt}
        disabled={testing || dirty || !initial.trim()}
        className="btn-secondary text-xs"
      >
        {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Send test'}
      </button>
      {initialMode === 'daily' && initial.trim() && (
        <button onClick={sendDigest} disabled={digesting || dirty} className="btn-secondary text-xs">
          {digesting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Send digest now'}
        </button>
      )}
    </div>
  );
}

interface Backup {
  name: string;
  path: string;
  created_at: string;
  size_bytes: number;
  files: { name: string; size: number }[];
}

function BackupCard() {
  const [list, setList] = useState<Backup[]>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  async function load() {
    setLoading(true);
    try {
      const r = await api.get<{ backups: Backup[] }>('/settings/backups');
      setList(r.backups);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);
  async function backupNow() {
    setBusy(true);
    try {
      await api.post('/settings/backup-db');
      toast.success('Backup created');
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'backup_failed');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card
      title="Backups"
      action={
        <button onClick={backupNow} disabled={busy} className="btn-primary text-xs">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Backup now'}
        </button>
      }
    >
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      ) : list.length === 0 ? (
        <p className="text-sm text-gray-500">No backups yet. Click <em>Backup now</em> to snapshot the SQLite DBs.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {list.slice(0, 10).map((b) => (
            <li key={b.name} className="flex items-center justify-between">
              <span className="font-mono text-xs text-gray-700 dark:text-gray-300">{b.name}</span>
              <span className="text-xs text-gray-500">
                {b.files.length} files · {(b.size_bytes / 1024 / 1024).toFixed(1)} MB
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-xs text-gray-500">
        Snapshots both <code>job_tracker.db</code> and <code>smart_fill_cache.db</code> via
        SQLite's atomic hot-backup. Restore manually from <code>backups/&lt;timestamp&gt;/</code>.
      </p>
    </Card>
  );
}

function ChangePasswordCard() {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!cur || next.length < 8) return;
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current: cur, next });
      toast.success('Password changed');
      setCur('');
      setNext('');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'change_failed');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title="Change admin password">
      <form onSubmit={submit} className="grid max-w-md gap-2">
        <input
          type="password"
          value={cur}
          onChange={(e) => setCur(e.target.value)}
          placeholder="Current password"
          className="input"
          autoComplete="current-password"
        />
        <input
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder="New password (min 8)"
          className="input"
          autoComplete="new-password"
        />
        <button type="submit" disabled={busy} className="btn-primary justify-self-start">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Change'}
        </button>
      </form>
    </Card>
  );
}

interface AuditRow {
  id: number;
  ts: string;
  method: string;
  path: string;
  status: number;
  ip: string;
  payload: string | null;
}

function AuditLogCard() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ items: AuditRow[] }>('/audit?limit=20')
      .then((r) => setRows(r.items))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card title="Recent admin actions (audit log)">
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No actions logged yet.</p>
      ) : (
        <ul className="space-y-1 text-xs">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center gap-2">
              <span className="w-32 shrink-0 text-gray-400">
                {new Date(r.ts.replace(' ', 'T') + 'Z').toLocaleString(undefined, {
                  month: 'short',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <span
                className={`w-14 shrink-0 font-mono text-xs ${
                  r.status >= 400 ? 'text-red-600' : 'text-green-600'
                }`}
              >
                {r.method}
              </span>
              <span className="truncate font-mono text-gray-700 dark:text-gray-300">{r.path}</span>
              <span className="ml-auto shrink-0 text-gray-400">{r.status}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
